import React from 'react'
import {
  Switch,
  Route,
  useLocation,
  useRouteMatch,
  useHistory
} from 'react-router'
import qs from 'qs'
import useSWR from 'swr'

import StatsProvider from '../../providers/StatsProvider'

import Dashboard from '../../pages/Dashboard'
import ErrorToast from '../../components/molecules/ErrorToast'

import { selectConvo } from '../../slices/convo'
import { useSocket } from '../../providers/SocketProvider'
import { useAuth } from '../../providers/AuthProvider'
import { useEnvironment } from '../../providers/EnvironmentProvider'
import { useTaskAPI, CALLBACK_PHASES } from '../../providers/TaskAPIProvider'
import { useAppSelector } from '../../hooks/redux'
import { Routes } from '..'

const DashboardRoute = () => {
  const location = useLocation()
  const usehistory = useHistory()
  const { path } = useRouteMatch()

  const { allConversations } = useAppSelector(selectConvo)
  const {
    dashboard: { initialConvosList },
    api: { rejectConversation }
  } = useSocket()
  const { isLoggedIn } = useAuth()
  const { user, loaded: envLoaded } = useEnvironment()
  const { registerCallback } = useTaskAPI()

  const [history, setHistory] = React.useState([])
  const [lastLocation, setLastLocation] = React.useState(null)
  const [pendingRemove, setPendingRemove] = React.useState(null)
  const [error, setError] = React.useState(null)

  const userID = React.useMemo(() => user?.id, [user])
  const canLoad = React.useMemo(
    () => isLoggedIn && envLoaded && Boolean(userID),
    [isLoggedIn, envLoaded, userID]
  )

  const {
    data: taskStats,
    error: taskError,
    mutate: taskMutate
  } = useSWR(() => {
    if (!canLoad) {
      return null
    }

    return `taskStats:${userID}`
  })
  const { data: chatStats, error: chatError } = useSWR(() => {
    if (!canLoad) {
      return null
    }

    return `chatStats:${userID}`
  })

  // Register mutate function to be called when task update happens
  React.useEffect(() => {
    registerCallback(
      CALLBACK_PHASES.UPDATE,
      'DashboardStats-TaskOpen',
      taskMutate
    )
  }, [registerCallback, taskMutate])

  React.useEffect(() => {
    if (!taskError && !chatError) {
      return
    }

    if (taskError) {
      setError({ err: taskError, auto: true })
    } else if (chatError) {
      setError({ err: chatError, auto: true })
    }
  }, [chatError, taskError])

  React.useEffect(() => {
    const params = qs.parse(location.search, { ignoreQueryPrefix: true })
    const { contacts } = params

    // last location is contacts, then remove convo
    if (lastLocation?.search.includes('contacts') && contacts) {
      setPendingRemove(contacts)
    }

    if (
      pendingRemove &&
      !contacts &&
      initialConvosList.length &&
      !initialConvosList.includes(pendingRemove)
    ) {
      sessionStorage.removeItem('viewing')
      sessionStorage.removeItem('blockView')
      setPendingRemove(null)
      rejectConversation(pendingRemove)
    }
  }, [
    lastLocation,
    location.search,
    pendingRemove,
    allConversations,
    initialConvosList,
    rejectConversation
  ])

  React.useEffect(() => {
    setHistory(old => [...old, location])
  }, [location])

  React.useEffect(() => {
    if (history.length > 1) {
      setLastLocation(history[history.length - 2])
    }
  }, [history])

  React.useEffect(() => {
    if (canLoad) {
      const hasToRedirect = localStorage.getItem('task-redirect-after-login')
      if (hasToRedirect) {
        localStorage.removeItem('task-redirect-after-login')
        usehistory.push(`${Routes.TASKS}/${hasToRedirect}`)
      }
    }
  }, [canLoad, usehistory])

  return (
    <Switch>
      <Route
        path={path}
        render={() => (
          <StatsProvider
            chatStats={chatStats}
            chatLoaded={!chatError && chatStats}
            taskStats={taskStats}
            taskLoaded={!taskError && taskStats}
          >
            <Dashboard />

            <ErrorToast error={error} />
          </StatsProvider>
        )}
      />
    </Switch>
  )
}

export default DashboardRoute
